import { useState, useCallback } from "react";
import { PageLayout } from "@/ui/layouts";
import { Card, CardContent, Button } from "@/ui/atoms";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import { Upload, X, CheckCircle2, AlertCircle, ShieldX, Loader2 } from "lucide-react";

/** Minimum number of files a single bulk upload must contain. */
export const MIN_FINJOURNAL_FILES = 1;

/** Maximum number of files a single bulk upload may contain. */
export const MAX_FINJOURNAL_FILES = 25;

type TUploadStatus = "pending" | "uploading" | "success" | "error";

type TFileEntry = {
    file: File;
    status: TUploadStatus;
    message?: string;
};

/**
 * View parameters (injected dependencies)
 */
export type TViewUploadStandaloneFinJournalsParams = {
    useCases: () => {
        onUploadFile: (file: File, contributorIds: string[]) => Promise<{ message: string }>;
        useContributorCandidates: () => {
            data: { id: string; label: string }[];
            loading: boolean;
        };
        canUpload: boolean;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewUploadStandaloneFinJournalsProps = Record<string, never>;

const isXlsx = (file: File) => file.name.toLowerCase().endsWith(".xlsx");

/** Two picks of the same file (same name, size and mtime) are the same file. */
const isSameFile = (a: File, b: File) =>
    a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/**
 * Creates the Upload Standalone FinJournals view component
 */
export const createViewUploadStandaloneFinJournals = ({
    useCases,
}: TViewUploadStandaloneFinJournalsParams) => {
    const ViewUploadStandaloneFinJournals: React.FC<TViewUploadStandaloneFinJournalsProps> = () => {
        const { onUploadFile, useContributorCandidates, canUpload } = useCases();

        const [isUploading, setIsUploading] = useState(false);
        const [uploadError, setUploadError] = useState<string | null>(null);
        const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
        const [entries, setEntries] = useState<TFileEntry[]>([]);
        const [dragActive, setDragActive] = useState(false);
        const [contributorIds, setContributorIds] = useState<string[]>([]);
        const contributorCandidates = useContributorCandidates();

        const handleFilesSelect = useCallback(
            (files: File[]) => {
                if (files.length === 0) return;

                const accepted = files.filter(isXlsx);
                const rejectedCount = files.length - accepted.length;
                // Re-picking a file already in the list is a no-op rather than a duplicate upload.
                const fresh = accepted.filter(
                    (file) => !entries.some((entry) => isSameFile(entry.file, file)),
                );
                const remaining = Math.max(MAX_FINJOURNAL_FILES - entries.length, 0);
                const admitted = fresh.slice(0, remaining);
                const overflow = fresh.length - admitted.length;

                const problems: string[] = [];
                if (rejectedCount > 0) {
                    problems.push(
                        `${rejectedCount} file${rejectedCount === 1 ? " was" : "s were"} skipped — only .xlsx files are allowed`,
                    );
                }
                if (overflow > 0) {
                    problems.push(
                        `You can upload up to ${MAX_FINJOURNAL_FILES} files at a time — ${overflow} file${overflow === 1 ? " was" : "s were"} not added`,
                    );
                }

                setUploadSuccess(null);
                setUploadError(problems.length > 0 ? problems.join(". ") : null);
                if (admitted.length > 0) {
                    setEntries((previous) => [
                        ...previous,
                        ...admitted.map((file) => ({ file, status: "pending" as const })),
                    ]);
                }
            },
            [entries],
        );

        const handleRemoveFile = useCallback((index: number) => {
            setEntries((previous) => previous.filter((_, i) => i !== index));
        }, []);

        const handleReset = useCallback(() => {
            setEntries([]);
            setUploadError(null);
            setUploadSuccess(null);
            setContributorIds([]);
        }, []);

        const pendingCount = entries.filter((entry) => entry.status !== "success").length;

        const handleUploadSubmit = useCallback(
            async (e: React.FormEvent) => {
                e.preventDefault();
                // Files that already uploaded are never sent twice; a retry only
                // re-sends the ones that failed.
                const queue = entries
                    .map((entry, index) => ({ entry, index }))
                    .filter(({ entry }) => entry.status !== "success");
                if (queue.length < MIN_FINJOURNAL_FILES) return;

                setIsUploading(true);
                setUploadError(null);
                setUploadSuccess(null);
                setEntries((previous) =>
                    previous.map((entry) =>
                        entry.status === "success"
                            ? entry
                            : { ...entry, status: "pending", message: undefined },
                    ),
                );

                let succeeded = 0;
                let failed = 0;

                // Sequential: each file is its own upload + background processing job,
                // so a failure never blocks the rest of the batch.
                for (const { entry, index } of queue) {
                    setEntries((previous) =>
                        previous.map((current, i) =>
                            i === index ? { ...current, status: "uploading" } : current,
                        ),
                    );
                    try {
                        const result = await onUploadFile(entry.file, contributorIds);
                        succeeded += 1;
                        setEntries((previous) =>
                            previous.map((current, i) =>
                                i === index
                                    ? {
                                          ...current,
                                          status: "success",
                                          message: result.message || "Uploaded",
                                      }
                                    : current,
                            ),
                        );
                    } catch (error) {
                        failed += 1;
                        console.error("Upload failed:", error);
                        const errorMessage =
                            error instanceof Error ? error.message : "Upload failed";
                        setEntries((previous) =>
                            previous.map((current, i) =>
                                i === index
                                    ? { ...current, status: "error", message: errorMessage }
                                    : current,
                            ),
                        );
                    }
                }

                if (succeeded > 0) {
                    setUploadSuccess(
                        `${succeeded} file${succeeded === 1 ? "" : "s"} uploaded successfully. Processing in background.`,
                    );
                }
                if (failed > 0) {
                    setUploadError(
                        `${failed} file${failed === 1 ? "" : "s"} failed to upload. See the list below.`,
                    );
                }
                setIsUploading(false);
            },
            [entries, contributorIds, onUploadFile],
        );

        const handleFileInputChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                handleFilesSelect(Array.from(e.target.files ?? []));
                // Allow re-picking the same file after it was removed from the list.
                e.target.value = "";
            },
            [handleFilesSelect],
        );

        const handleDrag = useCallback((e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.type === "dragenter" || e.type === "dragover") {
                setDragActive(true);
            } else if (e.type === "dragleave") {
                setDragActive(false);
            }
        }, []);

        const handleDrop = useCallback(
            (e: React.DragEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setDragActive(false);
                handleFilesSelect(Array.from(e.dataTransfer.files ?? []));
            },
            [handleFilesSelect],
        );

        if (!canUpload) {
            return (
                <PageLayout
                    title="Upload FinJournals"
                    description="Upload standalone Financial Journals files for processing"
                >
                    <Card className="max-w-2xl">
                        <CardContent className="pt-6">
                            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                <div className="mb-4 rounded-full bg-muted p-6">
                                    <ShieldX className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">Upload Not Available</h3>
                                <p className="text-sm text-muted-foreground max-w-md">
                                    Financial Journals upload is only available for Business
                                    Partners, Account Managers, and Accountants with upload
                                    permissions enabled.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </PageLayout>
            );
        }

        const isAtLimit = entries.length >= MAX_FINJOURNAL_FILES;

        return (
            <PageLayout
                title="Upload FinJournals"
                description="Upload standalone Financial Journals files for processing"
            >
                <Card className="max-w-2xl">
                    <CardContent className="pt-6">
                        <form onSubmit={handleUploadSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="file" className="block text-sm font-medium mb-1">
                                    Select Files <span className="text-destructive">*</span>
                                </label>
                                <p className="text-sm text-muted-foreground mb-2">
                                    Only .xlsx files are accepted — up to {MAX_FINJOURNAL_FILES}{" "}
                                    files at a time
                                </p>
                                <div
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                                        isAtLimit
                                            ? "border-border bg-muted/30 opacity-60"
                                            : dragActive
                                              ? "border-primary bg-primary/5"
                                              : "border-border hover:border-primary/50"
                                    }`}
                                >
                                    <input
                                        type="file"
                                        id="file"
                                        accept=".xlsx"
                                        multiple
                                        onChange={handleFileInputChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        disabled={isUploading || isAtLimit}
                                    />
                                    <div className="space-y-2">
                                        <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
                                        {isAtLimit ? (
                                            <div className="text-sm text-muted-foreground">
                                                Maximum of {MAX_FINJOURNAL_FILES} files reached —
                                                remove one to add another
                                            </div>
                                        ) : (
                                            <div className="text-sm text-muted-foreground">
                                                <span className="font-medium text-primary cursor-pointer hover:underline">
                                                    Click to upload
                                                </span>{" "}
                                                or drag and drop
                                            </div>
                                        )}
                                        <div className="text-xs text-muted-foreground">
                                            Excel files (.xlsx) only
                                        </div>
                                    </div>
                                </div>

                                {entries.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        <p className="text-sm font-medium text-muted-foreground">
                                            {entries.length} of {MAX_FINJOURNAL_FILES} files
                                            selected
                                        </p>
                                        {entries.map((entry, index) => (
                                            <div
                                                key={`${entry.file.name}-${entry.file.lastModified}-${index}`}
                                                className="flex items-center justify-between gap-3 p-3 border rounded-lg bg-muted/50"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <span className="block text-sm truncate">
                                                        {entry.file.name}
                                                    </span>
                                                    {entry.status === "error" && entry.message && (
                                                        <span className="block text-xs text-destructive">
                                                            {entry.message}
                                                        </span>
                                                    )}
                                                </div>
                                                {entry.status === "uploading" && (
                                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                                                )}
                                                {entry.status === "success" && (
                                                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                                )}
                                                {entry.status === "error" && (
                                                    <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                                                )}
                                                <button
                                                    type="button"
                                                    aria-label={`Remove ${entry.file.name}`}
                                                    onClick={() => handleRemoveFile(index)}
                                                    className="p-1 hover:bg-muted rounded shrink-0"
                                                    disabled={isUploading}
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-3">
                                <label
                                    htmlFor="contributors"
                                    className="text-sm font-medium w-28 shrink-0"
                                >
                                    Contributors
                                </label>
                                <div className="min-w-0 flex-1">
                                    <MultiSelect
                                        options={contributorCandidates.data}
                                        selectedIds={contributorIds}
                                        onChange={setContributorIds}
                                        placeholder="Select contributors..."
                                        emptyMessage="No contributors available"
                                        disabled={isUploading || contributorCandidates.loading}
                                    />
                                </div>
                            </div>

                            {uploadError && (
                                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    <span>{uploadError}</span>
                                </div>
                            )}

                            {uploadSuccess && (
                                <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 flex items-start gap-2">
                                    <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    <span>{uploadSuccess}</span>
                                </div>
                            )}

                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleReset}
                                    disabled={
                                        isUploading ||
                                        (entries.length === 0 && !uploadError && !uploadSuccess)
                                    }
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isUploading || pendingCount < MIN_FINJOURNAL_FILES}
                                >
                                    {isUploading ? (
                                        <>
                                            <Upload className="mr-2 h-4 w-4 animate-spin" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="mr-2 h-4 w-4" />
                                            Upload
                                            {pendingCount > 1 ? ` ${pendingCount} files` : ""}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </PageLayout>
        );
    };

    return ViewUploadStandaloneFinJournals;
};
